// src/components/panels/LiveSpeedCam.tsx
// MEGA DASHBOARD - All metrics, rainbow style, overwhelm mode
// Uses worker-shell architecture for API communication
import React, { useState, useEffect, useRef } from 'react';
import './LiveSpeedCam.css';
import { getMaxWorkers } from '../../hooks/usePipelineConfig';
import { synapseWorkerBridge } from '../../lib/SynapseWorkerBridge';
import type { MetricsResponse, StatusResponse, ConfigResponse } from '../../types/worker-messages';
import {
  Clock, Users, ListTodo, CheckCircle2, XCircle, DollarSign,
  Activity, Gauge, Timer, Percent, AlertTriangle, Coins,
  Zap, Cpu, HardDrive, Layers, RefreshCw, Rocket
} from 'lucide-react';

interface TaskMetrics {
  uptime_seconds: number;
  active_tasks: number;
  active_workers: number;
  worker_count: number;
  completed_tasks: number;
  completed_today: number;
  failed_tasks: number;
  queued_tasks: number;
  queue_depth: number;
  total_spend: number;
  total_tokens: number;
  tokens_per_minute: number;
  tasks_per_hour: number;
  tasks_per_minute: number;
  avg_task_duration_seconds: number;
  worker_utilization: number;
  error_rate: number;
  cpu_percent: number;
  memory_mb: number;
  total_cost_usd: number;
}

interface WebSocketMessage {
  type: string;
  data?: any;
  stats?: any;
  active?: number;
  completed?: number;
  failed?: number;
  tasks?: any[];
}

interface LiveSpeedCamProps {
  children?: React.ReactNode;
}

interface WorkerConfig {
  max_workers: number;
  concurrent_limit: number;
}

interface RateLimits {
  tasks_per_hour: number;
  api_calls_per_minute: number;
}

interface DashboardConfig {
  worker_config: WorkerConfig;
  rate_limits: RateLimits;
}

const LiveSpeedCam: React.FC<LiveSpeedCamProps> = ({ children }) => {
  // Get max workers from localStorage or default
  const localMaxWorkers = getMaxWorkers();

  // Config from API with fallback defaults - prioritize localStorage
  const [config, setConfig] = useState<DashboardConfig>({
    worker_config: {
      max_workers: localMaxWorkers,
      concurrent_limit: localMaxWorkers * 2,
    },
    rate_limits: {
      tasks_per_hour: 120,
      api_calls_per_minute: 2000,
    },
  });

  const [metrics, setMetrics] = useState<TaskMetrics>({
    uptime_seconds: 0,
    active_tasks: 0,
    active_workers: 0,
    worker_count: 0,
    completed_tasks: 0,
    completed_today: 0,
    failed_tasks: 0,
    queued_tasks: 0,
    queue_depth: 0,
    total_spend: 0,
    total_tokens: 0,
    tokens_per_minute: 0,
    tasks_per_hour: 0,
    tasks_per_minute: 0,
    avg_task_duration_seconds: 0,
    worker_utilization: 0,
    error_rate: 0,
    cpu_percent: 0,
    memory_mb: 0,
    total_cost_usd: 0,
  });

  // Load config from API on mount, but prioritize localStorage overrides
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await synapseWorkerBridge.send<ConfigResponse>('config:synapse', {});
        if (response.type !== 'response') throw new Error('Config fetch failed');
        const data = response.payload as any;

        // Check localStorage for user overrides
        const localOverrides = localStorage.getItem('pipeline-config-overrides');
        let maxWorkersOverride = localMaxWorkers;
        if (localOverrides) {
          try {
            const parsed = JSON.parse(localOverrides);
            if (parsed.workers?.pool_size) {
              maxWorkersOverride = parsed.workers.pool_size;
            }
          } catch {}
        }

        // API returns { worker_config, rate_limits }
        // Prioritize localStorage max_workers over API
        setConfig(prev => ({
          worker_config: data.worker_config ? {
            ...data.worker_config,
            max_workers: maxWorkersOverride,
          } : prev.worker_config,
          rate_limits: data.rate_limits || prev.rate_limits,
        }));
      } catch {
        // Fallback to default values already set in state
        console.warn('Failed to fetch config from API, using defaults');
      }
    };

    fetchConfig();

    // Listen for localStorage changes from PipelineConfigPage
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'pipeline-config-overrides' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.workers?.pool_size) {
            setConfig(prev => ({
              ...prev,
              worker_config: {
                ...prev.worker_config,
                max_workers: parsed.workers.pool_size,
              },
            }));
          }
        } catch {}
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isClearing, setIsClearing] = useState(false);
  const [clearAlert, setClearAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  // Local uptime ticker - runs independently, syncs once on load
  const [displayUptime, setDisplayUptime] = useState(0);
  const hasInitialized = useRef(false);
  const serverUptimeRef = useRef(0);  // Track server value without triggering renders

  // Tick uptime locally every second - completely independent
  useEffect(() => {
    const ticker = setInterval(() => {
      setDisplayUptime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(ticker);
  }, []);

  // Only sync ONCE on initial load when we get first server value
  useEffect(() => {
    if (!hasInitialized.current && metrics.uptime_seconds > 0) {
      hasInitialized.current = true;
      serverUptimeRef.current = metrics.uptime_seconds;
      setDisplayUptime(metrics.uptime_seconds);
    } else {
      // Just track server value for reference, don't update display
      serverUptimeRef.current = metrics.uptime_seconds;
    }
  }, [metrics.uptime_seconds]);

  // Format uptime - compact single line with seconds
  const formatUptime = (seconds: number) => {
    if (!seconds || seconds < 0) return '0s';
    const s = Math.floor(seconds % 60);
    const m = Math.floor((seconds % 3600) / 60);
    const h = Math.floor((seconds % 86400) / 3600);
    const d = Math.floor(seconds / 86400);
    if (d > 0) return `${d}d${h}h${m}m${s}s`;
    if (h > 0) return `${h}h${m}m${s}s`;
    if (m > 0) return `${m}m${s}s`;
    return `${s}s`;
  };

  // Format numbers
  const formatNumber = (n: number) => {
    if (!n || n < 0) return '0';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  // Format rate
  const formatRate = (n: number) => {
    if (!n || n < 0) return '0';
    return n.toFixed(1);
  };

  // CPU color based on threshold (green < 40, amber 40-70, red > 70)
  const getCpuColor = (percent: number) => {
    if (percent < 40) return '#22c55e'; // green
    if (percent < 70) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  // CPU stripe class based on threshold
  const getCpuStripe = (percent: number) => {
    if (percent < 40) return 'stripe-emerald';
    if (percent < 70) return 'stripe-amber';
    return 'stripe-rose';
  };

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/api/synapse/ws/tasks?token=synapse-dev`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('LiveSpeedCam WS connected');
          setConnectionStatus('connected');
        };

        ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            handleWebSocketMessage(message);
            setLastUpdate(new Date());
          } catch (error) {
            // Silently handle parse errors
          }
        };

        ws.onerror = () => {
          if (connectionStatus === 'connected') {
            setConnectionStatus('disconnected');
          }
        };

        ws.onclose = () => {
          setConnectionStatus('disconnected');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 2000);
        };
      } catch (error) {
        setConnectionStatus('disconnected');
      }
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Polling fallback - only when WebSocket is NOT connected
  useEffect(() => {
    // Skip polling if WebSocket is connected
    if (connectionStatus === 'connected') return;

    const fetchData = async () => {
      try {
        const [metricsRes, statusRes] = await Promise.all([
          synapseWorkerBridge.send<MetricsResponse>('metrics:get', {}),
          synapseWorkerBridge.send<StatusResponse>('status:get', {})
        ]);

        if (metricsRes.type === 'response' && statusRes.type === 'response') {
          const metricsData = metricsRes.payload as any;
          const statusData = statusRes.payload as any;

          setMetrics(prev => ({
            ...prev,
            uptime_seconds: statusData.uptime_seconds || 0,
            active_tasks: metricsData.active_tasks ?? 0,
            active_workers: statusData.active_workers ?? 40,
            worker_count: config.worker_config.max_workers,
            // Use METRICS for historical totals (completed, tokens, cost) - status only has session data
            completed_tasks: metricsData.completed_tasks || 0,
            completed_today: statusData.completed_tasks || metricsData.completed_tasks || prev.completed_today,
            failed_tasks: statusData.failed_tasks || metricsData.failed_tasks || 0,
            queued_tasks: statusData.queued_tasks || 0,
            queue_depth: statusData.queued_tasks || metricsData.queue_depth || 0,
            tasks_per_hour: statusData.tasks_per_hour || 0,
            tasks_per_minute: metricsData.tasks_per_minute || 0,
            avg_task_duration_seconds: metricsData.avg_task_duration_seconds || statusData.avg_task_duration_sec || 0,
            worker_utilization: metricsData.worker_utilization || 0,
            error_rate: statusData.error_rate || 0,
            cpu_percent: metricsData.cpu_percent ?? statusData.cpu_percent ?? prev.cpu_percent,
            memory_mb: metricsData.memory_mb ?? statusData.memory_mb ?? prev.memory_mb,
            total_tokens: metricsData.total_tokens || 0,
            tokens_per_minute: metricsData.tokens_per_minute || 0,
            total_cost_usd: metricsData.total_cost_usd || 0,
          }));
          setLastUpdate(new Date());
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchData(); // Initial fetch
    const interval = setInterval(fetchData, 3000); // Poll every 3s when no WS
    return () => clearInterval(interval);
  }, [connectionStatus]);

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    // Handle LIVE metrics broadcast (every 500ms from server)
    if (message.type === 'metrics') {
      const m = message as any;
      setMetrics(prev => ({
        ...prev,
        uptime_seconds: m.uptime_seconds ?? prev.uptime_seconds,
        active_workers: m.active_workers ?? prev.active_workers,
        active_tasks: m.active_tasks ?? prev.active_tasks,
        completed_tasks: m.completed_tasks ?? prev.completed_tasks,
        failed_tasks: m.failed_tasks ?? prev.failed_tasks,
        queued_tasks: m.queued_tasks ?? prev.queued_tasks,
        tasks_per_hour: m.tasks_per_hour ?? prev.tasks_per_hour,
        tasks_per_minute: m.tasks_per_minute ?? prev.tasks_per_minute,
        worker_utilization: m.worker_utilization ?? prev.worker_utilization,
        avg_task_duration_seconds: m.avg_task_duration_seconds ?? prev.avg_task_duration_seconds,
        cpu_percent: m.cpu_percent ?? prev.cpu_percent,
        memory_mb: m.memory_mb ?? prev.memory_mb,
        total_tokens: m.total_tokens ?? prev.total_tokens,
        tokens_per_minute: m.tokens_per_minute ?? prev.tokens_per_minute,
        total_cost_usd: m.total_cost_usd ?? prev.total_cost_usd,
      }));
      return;  // Skip other handlers for metrics
    }
    // Handle stats message - DON'T update completed_tasks (session-only, use metrics message instead)
    if (message.type === 'stats') {
      setMetrics(prev => ({
        ...prev,
        active_tasks: message.active ?? prev.active_tasks,
        failed_tasks: message.failed ?? prev.failed_tasks,
      }));
    }
    // Handle init message - DON'T update completed_tasks (session-only, use metrics message instead)
    if (message.type === 'init' && message.stats) {
      setMetrics(prev => ({
        ...prev,
        active_tasks: message.stats.active ?? prev.active_tasks,
        failed_tasks: message.stats.failed ?? prev.failed_tasks,
      }));
    }
    // Handle data payload - only update fields that are present and not undefined
    if (message.data) {
      setMetrics(prev => {
        const updated = { ...prev };
        for (const [key, value] of Object.entries(message.data)) {
          if (value !== undefined && value !== null) {
            (updated as any)[key] = value;
          }
        }
        return updated;
      });
    }
  };

  // Clear pipeline handler
  const handleClearPipeline = async () => {
    const totalTasks = (metrics.queued_tasks || 0) + (metrics.active_tasks || 0);

    if (totalTasks === 0) {
      setClearAlert({ message: 'No tasks to clear', type: 'error' });
      setTimeout(() => setClearAlert(null), 3000);
      return;
    }

    const confirmed = window.confirm(
      `⚠️ Clear ALL pipeline tasks?\n\n` +
      `This will cancel:\n` +
      `• ${metrics.queued_tasks || 0} queued tasks\n` +
      `• ${metrics.active_tasks || 0} active tasks\n\n` +
      `Total: ${totalTasks} tasks will be cleared.\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    setIsClearing(true);
    try {
      const response = await fetch('/api/synapse/clear?token=synapse-dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Clear failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const cleared = result.cleared || totalTasks;

      setClearAlert({
        message: `✓ Pipeline cleared: ${cleared} tasks removed`,
        type: 'success'
      });

      // Refresh metrics immediately
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Clear pipeline error:', error);
      setClearAlert({
        message: `✗ Clear failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setIsClearing(false);
      setTimeout(() => setClearAlert(null), 5000);
    }
  };

  // Use total_cost_usd from API which already includes session + persisted totals
  const totalSpend = metrics.total_cost_usd || 0;

  return (
    <div className="live-speed-cam" data-component="LiveSpeedCam" data-file="src/components/panels/LiveSpeedCam.tsx">
      {/* Header with rainbow shine */}
      <div className="speed-cam-header">
        <h2>
          <span className="icon">⚡</span>
          <span className="rainbow-text">LiveSpeedCam</span>
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Emergency Clear Button */}
          <button
            onClick={handleClearPipeline}
            disabled={isClearing}
            className={`clear-pipeline-btn ${(metrics.queued_tasks || 0) > 0 ? 'has-tasks' : ''}`}
            title={`Clear all pipeline tasks (${(metrics.queued_tasks || 0) + (metrics.active_tasks || 0)} total)`}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              border: 'none',
              borderRadius: '6px',
              background: (metrics.queued_tasks || 0) > 0
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'rgba(239, 68, 68, 0.2)',
              color: (metrics.queued_tasks || 0) > 0 ? '#ffffff' : '#ef4444',
              cursor: isClearing ? 'wait' : 'pointer',
              boxShadow: (metrics.queued_tasks || 0) > 0
                ? '0 0 20px rgba(239, 68, 68, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                : 'none',
              animation: (metrics.queued_tasks || 0) > 0 ? 'pulse-glow 2s ease-in-out infinite' : 'none',
              transition: 'all 0.2s ease',
              opacity: isClearing ? 0.6 : 1,
            }}
          >
            {isClearing ? '⏳ Clearing...' : '🗑️ Clear Pipeline'}
          </button>

          <div className="connection-indicator">
            <span className={`status-dot ${connectionStatus}`} style={connectionStatus === 'connected' ? { background: '#22c55e', boxShadow: '0 0 10px rgba(34, 197, 94, 0.7)' } : {}} />
            <span className="status-text" style={connectionStatus === 'connected' ? { color: '#22c55e' } : {}}>
              {connectionStatus === 'connected' ? 'WS LIVE' : connectionStatus === 'connecting' ? '...' : 'Poll'}
            </span>
          </div>
        </div>
      </div>

      {/* Alert Toast */}
      {clearAlert && (
        <div
          style={{
            position: 'fixed',
            top: '80px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            background: clearAlert.type === 'success'
              ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: clearAlert.type === 'success'
              ? '0 4px 20px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              : '0 4px 20px rgba(239, 68, 68, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          {clearAlert.message}
        </div>
      )}

      {/* MEGA metrics grid - ALL THE DATA */}
      <div className="metrics-grid mega">
        {/* Row 1: Core metrics */}
        <div className="metric-card stripe-purple glow">
          <Clock className="metric-icon-svg" style={{ color: '#a855f7' }} />
          <div className="metric-label">Uptime</div>
          <div className="metric-value" style={{ color: '#a855f7' }}>
            {formatUptime(displayUptime)}
          </div>
          <div className="metric-sub">server</div>
        </div>

        <div className="metric-card stripe-orange glow">
          <Users className="metric-icon-svg" style={{ color: '#f59e0b' }} />
          <div className="metric-label">Workers</div>
          <div className="metric-value" style={{ color: '#f59e0b' }}>
            {metrics.active_tasks ?? 0}/{config.worker_config.max_workers}
          </div>
          <div className="metric-sub">active/max</div>
        </div>

        <div className="metric-card stripe-yellow glow">
          <ListTodo className="metric-icon-svg" style={{ color: '#eab308' }} />
          <div className="metric-label">Queued</div>
          <div className="metric-value" style={{ color: '#eab308' }}>
            {metrics.queued_tasks}
          </div>
          <div className="metric-sub">pending</div>
        </div>

        <div className="metric-card stripe-green glow">
          <CheckCircle2 className="metric-icon-svg" style={{ color: '#22c55e' }} />
          <div className="metric-label">Completed</div>
          <div className="metric-value" style={{ color: '#22c55e' }}>
            {formatNumber(metrics.completed_tasks)}
          </div>
          <div className="metric-sub">total tasks</div>
        </div>

        <div className="metric-card stripe-red glow">
          <XCircle className="metric-icon-svg" style={{ color: '#ef4444' }} />
          <div className="metric-label">Failed</div>
          <div className="metric-value" style={{ color: '#ef4444' }}>
            {metrics.failed_tasks}
          </div>
          <div className="metric-sub">errors</div>
        </div>

        <div className="metric-card stripe-pink glow">
          <DollarSign className="metric-icon-svg" style={{ color: '#ec4899' }} />
          <div className="metric-label">API Cost</div>
          <div className="metric-value" style={{ color: '#ec4899' }}>
            ${totalSpend.toFixed(2)}
          </div>
          <div className="metric-sub">total spend</div>
        </div>

        {/* Row 2: Performance metrics */}
        <div className="metric-card stripe-cyan glow">
          <Activity className="metric-icon-svg" style={{ color: '#22d3ee' }} />
          <div className="metric-label">Tasks/Hour</div>
          <div className="metric-value" style={{ color: '#22d3ee' }}>
            {formatRate(metrics.tasks_per_hour)}
          </div>
          <div className="metric-sub">throughput</div>
        </div>

        <div className="metric-card stripe-blue glow">
          <Gauge className="metric-icon-svg" style={{ color: '#3b82f6' }} />
          <div className="metric-label">Tasks/Min</div>
          <div className="metric-value" style={{ color: '#3b82f6' }}>
            {formatRate(metrics.tasks_per_minute)}
          </div>
          <div className="metric-sub">rate</div>
        </div>

        <div className="metric-card stripe-indigo glow">
          <Timer className="metric-icon-svg" style={{ color: '#6366f1' }} />
          <div className="metric-label">Avg Time</div>
          <div className="metric-value" style={{ color: '#6366f1' }}>
            {formatRate(metrics.avg_task_duration_seconds)}s
          </div>
          <div className="metric-sub">per task</div>
        </div>

        <div className="metric-card stripe-teal glow">
          <Percent className="metric-icon-svg" style={{ color: '#14b8a6' }} />
          <div className="metric-label">Utilization</div>
          <div className="metric-value" style={{ color: '#14b8a6' }}>
            {(metrics.worker_utilization * 100).toFixed(0)}%
          </div>
          <div className="utilization-bar">
            <div className="utilization-fill" style={{ width: `${(metrics.worker_utilization || 0) * 100}%` }} />
          </div>
          <div className="metric-sub">workers</div>
        </div>

        <div className="metric-card stripe-violet glow">
          <Coins className="metric-icon-svg" style={{ color: '#8b5cf6' }} />
          <div className="metric-label">Tokens</div>
          <div className="metric-value" style={{ color: '#8b5cf6' }}>
            {formatNumber(metrics.total_tokens)}
          </div>
          <div className="metric-sub">total used</div>
        </div>

        {/* Row 3: System metrics */}
        <div className="metric-card stripe-emerald glow">
          <Zap className="metric-icon-svg" style={{ color: '#10b981' }} />
          <div className="metric-label">Tokens/Min</div>
          <div className="metric-value" style={{ color: '#10b981' }}>
            {formatRate(metrics.tokens_per_minute)}
          </div>
          <div className="metric-sub">burn rate</div>
        </div>

        <div className={`metric-card ${getCpuStripe(metrics.cpu_percent)} glow`}>
          <Cpu className="metric-icon-svg" style={{ color: getCpuColor(metrics.cpu_percent) }} />
          <div className="metric-label">CPU</div>
          <div className="metric-value" style={{ color: getCpuColor(metrics.cpu_percent) }}>
            {metrics.cpu_percent.toFixed(0)}%
          </div>
          <div className="cpu-bar">
            <div className="cpu-fill" style={{ width: `${metrics.cpu_percent}%`, backgroundColor: getCpuColor(metrics.cpu_percent) }} />
          </div>
          <div className="metric-sub">system</div>
        </div>

        <div className="metric-card stripe-sky glow">
          <HardDrive className="metric-icon-svg" style={{ color: '#0ea5e9' }} />
          <div className="metric-label">Memory</div>
          <div className="metric-value" style={{ color: '#0ea5e9' }}>
            {metrics.memory_mb >= 1000 ? (metrics.memory_mb / 1000).toFixed(3) + 'G' : metrics.memory_mb.toFixed(2) + 'MB'}
          </div>
          <div className="metric-sub">/ 7.9GB</div>
        </div>

        <div className="metric-card stripe-lime glow">
          <Layers className="metric-icon-svg" style={{ color: '#84cc16' }} />
          <div className="metric-label">Queue Fill</div>
          <div className="metric-value" style={{ color: '#84cc16' }}>
            {Math.min(((metrics.queued_tasks || 0) / 100) * 100, 100).toFixed(0)}%
          </div>
          <div className="queue-bar">
            <div className="queue-fill" style={{ width: `${Math.min((metrics.queued_tasks || 0), 100)}%` }} />
          </div>
          <div className="metric-sub">capacity</div>
        </div>

        <div className="metric-card stripe-fuchsia glow">
          <RefreshCw className="metric-icon-svg" style={{ color: '#d946ef' }} />
          <div className="metric-label">Last Update</div>
          <div className="metric-value" style={{ color: '#d946ef', fontSize: '14px' }}>
            {lastUpdate.toLocaleTimeString()}
          </div>
          <div className="metric-sub">refresh</div>
        </div>

        <div className="metric-card stripe-gradient glow pulse">
          <Rocket className="metric-icon-svg rainbow-icon" />
          <div className="metric-label">PIPELINE</div>
          <div className="metric-value rainbow-value">
            {metrics.active_workers > 0 ? 'LIVE' : 'IDLE'}
          </div>
          <div className="metric-sub">{metrics.active_workers > 30 ? 'BLAZING' : metrics.active_workers > 10 ? 'HOT' : 'WARMING'}</div>
        </div>
      </div>

      {/* Children content (graphs, activity feed, etc) */}
      <div className="speed-cam-content">
        {children}
      </div>

      {/* Footer */}
      <div className="speed-cam-footer">
        <span>ControlDeck v2.2</span>
        <span>•</span>
        <span>dev.mothershipio.ca</span>
        <span>•</span>
        <span className="refresh-icon pulse">🔄</span>
        <span>1s</span>
        <span>•</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default LiveSpeedCam;
